import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { toast } from 'sonner';

const emptyForm = (kind) => ({
  codigo: '', descricao: '', complemento: '', categoria: '', custo: '', valor: '',
  unidade: kind === 'servico' ? 'SV' : 'UN',
  duracao_min: '', capacidade_atendimento: '', mostrar_agendamento: false
});

// custo/valor são guardados como centavos (inteiro) no form; exibidos formatados em pt-BR
const centavosToDisplay = (centavos) =>
  centavos === '' ? '' : (Number(centavos) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const reaisToCentavos = (reais) => reais ? Math.round(Number(reais) * 100) : '';

const LABELS = {
  produto: { nome: 'produto', nomeCap: 'Produto', valorLabel: 'Preço de venda (R$)', testid: 'product' },
  servico: { nome: 'serviço', nomeCap: 'Serviço', valorLabel: 'Valor de venda (R$)', testid: 'service' },
};

// kind: 'produto' | 'servico'
// editing: item existente (edição) ou null (novo cadastro)
// initialDescricao: pré-preenche a descrição ao abrir pra um cadastro novo
// onSave(payload, editingId): faz o POST/PUT e retorna o item salvo
export default function CatalogItemModal({ open, kind, editing, initialDescricao, categories = [], showAgendaFields = false, onSave, onClose, onSaved }) {
  const [form, setForm] = useState(emptyForm(kind));
  const [saving, setSaving] = useState(false);
  const labels = LABELS[kind];

  useEffect(() => {
    if (!open) return;
    setForm(editing
      ? { ...emptyForm(kind), ...editing, duracao_min: editing.duracao_min ?? '', capacidade_atendimento: editing.capacidade_atendimento ?? '', custo: reaisToCentavos(editing.custo), valor: reaisToCentavos(editing.valor) }
      : { ...emptyForm(kind), descricao: (initialDescricao || '').toUpperCase() });
  }, [open, editing, kind, initialDescricao]);

  if (!open) return null;

  const set = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));
  const setOptionalNum = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value === '' ? '' : Number(e.target.value) }));
  const setMoney = (k) => (e) => {
    const digits = e.target.value.replace(/\D/g, '');
    setForm(p => ({ ...p, [k]: digits === '' ? '' : Number(digits) }));
  };

  const save = async () => {
    if (!form.descricao) { toast.error('Descrição obrigatória'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        custo: form.custo === '' ? 0 : form.custo / 100,
        valor: form.valor === '' ? 0 : form.valor / 100,
        duracao_min: form.duracao_min === '' ? null : form.duracao_min,
        capacidade_atendimento: form.capacidade_atendimento === '' ? null : form.capacidade_atendimento,
      };
      const saved = await onSave(payload, editing?.id || null);
      toast.success(editing ? `${labels.nomeCap} atualizado` : `${labels.nomeCap} cadastrado`);
      onSaved?.(saved);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center p-4 overflow-y-auto">
      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-lg my-8">
        <div className="flex items-center justify-between p-5 border-b border-zinc-800">
          <h2 className="font-heading font-semibold text-white">{editing ? 'Editar' : 'Novo'} {labels.nome}</h2>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X size={20} /></button>
        </div>
        <div className="p-5 grid grid-cols-2 gap-4">
          <div>
            <label className="field-label">Código</label>
            <input className="field-input" value={form.codigo} onChange={set('codigo')} />
          </div>
          <div>
            <label className="field-label">Unidade</label>
            <input className="field-input" value={form.unidade} onChange={set('unidade')} />
          </div>
          <div className="col-span-2">
            <label className="field-label">Descrição *</label>
            <input data-testid={`${labels.testid}-desc-input`} className="field-input" value={form.descricao} onChange={e => setForm(p => ({ ...p, descricao: e.target.value.toUpperCase() }))} />
          </div>
          <div className="col-span-2">
            <label className="field-label">Complemento</label>
            <input className="field-input" value={form.complemento} onChange={e => setForm(p => ({ ...p, complemento: e.target.value.toUpperCase() }))} />
          </div>
          <div>
            <label className="field-label">Categoria</label>
            <select className="field-select" value={form.categoria} onChange={set('categoria')}>
              <option value="">Selecionar...</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="field-label">Custo (R$)</label>
            <input type="text" inputMode="decimal" className="field-input" value={centavosToDisplay(form.custo)} onChange={setMoney('custo')} />
          </div>
          <div>
            <label className="field-label">{labels.valorLabel}</label>
            <input type="text" inputMode="decimal" className="field-input" value={centavosToDisplay(form.valor)} onChange={setMoney('valor')} />
          </div>
          {kind === 'servico' && showAgendaFields && (
            <>
              <div>
                <label className="field-label">Duração (min)</label>
                <input type="number" min="0" step="1" placeholder="Usa o padrão da agenda" className="field-input" value={form.duracao_min} onChange={setOptionalNum('duracao_min')} />
              </div>
              <div>
                <label className="field-label">Capacidade por atendimento</label>
                <input type="number" min="0" step="1" placeholder="Usa o padrão da agenda" className="field-input" value={form.capacidade_atendimento} onChange={setOptionalNum('capacidade_atendimento')} disabled={form.duracao_min === ''} />
              </div>
              <div className="col-span-2 flex items-center gap-2">
                <input type="checkbox" id="mostrar_agendamento" checked={form.mostrar_agendamento} onChange={e => setForm(p => ({ ...p, mostrar_agendamento: e.target.checked }))} />
                <label htmlFor="mostrar_agendamento" className="field-label !mb-0">Aparecer no agendamento (menu "Selecione o serviço" da agenda do cliente)</label>
              </div>
            </>
          )}
        </div>
        <div className="p-5 border-t border-zinc-800 flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm text-zinc-400 hover:text-white border border-zinc-700 rounded-xl transition-colors">Cancelar</button>
          <button data-testid={`save-${labels.testid}-btn`} onClick={save} disabled={saving}
            className="bg-orange-500 hover:bg-orange-400 disabled:opacity-50 text-white font-semibold px-5 py-2 rounded-xl transition-colors text-sm">
            {saving ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
}
